import React, { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, Package, TrendingUp, DollarSign, Activity, BarChart3 } from 'lucide-react';

const ProductDetailsModal = ({ product, onClose, onUpdate, onAnalyze, readOnly = false }) => {
    const [config, setConfig] = useState({
        min_inventory_qty: 0,
        pack_size: 1,
        supplier: '',
        purchasing_price: 0,
        days_coverage: 7
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (product) {
            setConfig({
                min_inventory_qty: product.min_inventory_qty || 0,
                pack_size: product.pack_size || 1,
                supplier: product.supplier || '',
                purchasing_price: product.purchasing_price || 0,
                days_coverage: 7
            });
        }
    }, [product]);

    if (!product) return null;

    const handleSave = async () => {
        setSaving(true);
        await onUpdate(product.id, {
            min_inventory_qty: Number(config.min_inventory_qty),
            pack_size: Number(config.pack_size),
            supplier: config.supplier,
            purchasing_price: Number(config.purchasing_price)
        });
        setSaving(false);
        onClose();
    };

    const getUrgencyStatus = () => {
        if (product.days_until_stockout <= 0) return { color: 'bg-red-100 text-red-800', text: 'AGOTADO', iconColor: 'text-red-600' };
        if (product.days_until_stockout <= 3) return { color: 'bg-orange-100 text-orange-800', text: 'CRÍTICO', iconColor: 'text-orange-600' };
        if (product.days_until_stockout <= 7) return { color: 'bg-yellow-100 text-yellow-800', text: 'BAJO', iconColor: 'text-yellow-600' };
        return { color: 'bg-green-100 text-green-800', text: 'NORMAL', iconColor: 'text-green-600' };
    };

    const status = getUrgencyStatus();

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden animate-in fade-in zoom-in duration-200">
                {/* Header */}
                <div className="px-8 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-100 text-blue-600 rounded-xl">
                            <Package size={24} />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-gray-900 leading-tight">{product.name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs font-mono text-gray-500 bg-gray-200/50 px-1.5 py-0.5 rounded">{product.internal_code}</span>
                                <span className="text-gray-300">•</span>
                                <span className="text-xs text-gray-500 font-medium">{product.barcode || 'Sin código de barras'}</span>
                            </div>
                            {product.is_mix && (
                                <div className="mt-2 flex items-center gap-2">
                                    <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-[10px] font-black rounded uppercase border border-indigo-200">Producto Combinado (Mix)</span>
                                    <span className="text-[10px] text-gray-400 font-medium italic">+ {product.mix_component}</span>
                                </div>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200/50 rounded-full transition-all">
                        <X size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                        {/* Left Column: Analysis & Insights */}
                        <div className="space-y-8">
                            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                    <TrendingUp size={16} /> Estado y Análisis
                                </h4>
                                <div className="flex gap-2">
                                    <div className={`px-3 py-1 rounded-full text-[10px] font-black tracking-wider ${status.color}`}>
                                        {status.text}
                                    </div>
                                    {product.abc_classification && (
                                        <div className={`px-3 py-1 rounded-full text-[10px] font-black tracking-wider border ${product.abc_classification === 'A' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                                            product.abc_classification === 'B' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                                'bg-gray-100 text-gray-700 border-gray-200'
                                            }`}>
                                            CLASE {product.abc_classification}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Main Metrics Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 p-4 rounded-xl border border-blue-100">
                                    <p className="text-[10px] text-blue-600 font-black uppercase tracking-wider mb-1">Stock Actual</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-black text-blue-900">{product.current_stock}</span>
                                        <span className="text-xs text-blue-600 font-bold uppercase tracking-tighter">unidades</span>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 p-4 rounded-xl border border-purple-100">
                                    <p className="text-[10px] text-purple-600 font-black uppercase tracking-wider mb-1">Sugerido</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-3xl font-black text-purple-900">{product.suggested_order_qty || 0}</span>
                                        <span className="text-xs text-purple-600 font-bold uppercase tracking-tighter">unidades</span>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-green-100/50 p-4 rounded-xl border border-green-100">
                                    <p className="text-[10px] text-green-600 font-black uppercase tracking-wider mb-1">Precio Venta</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-xl font-black text-green-900">
                                            {new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(product.standard_price || 0)}
                                        </span>
                                    </div>
                                </div>
                                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 p-4 rounded-xl border border-amber-100">
                                    <p className="text-[10px] text-amber-600 font-black uppercase tracking-wider mb-1">Rentabilidad Bruta</p>
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-xl font-black text-amber-900">
                                            {(() => {
                                                const ivaFactor = 1 + (Number(product.taxes_iva || 19) / 100);
                                                const priceNoVat = (product.standard_price || 0) / ivaFactor;
                                                const cost = Number(config.purchasing_price || 0);
                                                if (priceNoVat <= 0) return '0%';
                                                const margin = ((priceNoVat - cost) / priceNoVat) * 100;
                                                return `${margin.toFixed(1)}%`;
                                            })()}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Detailed Stats */}
                            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 space-y-4">
                                <h5 className="text-[11px] font-black text-gray-400 uppercase tracking-widest flex items-center gap-2 mb-2">
                                    <BarChart3 size={14} /> Dinámica de Inventario
                                </h5>
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 font-medium mb-1 flex items-center gap-1.5">
                                            <Activity size={12} className="text-indigo-500" /> Consumo Promedio
                                        </span>
                                        <span className="text-lg font-black text-gray-800">
                                            {Number(product.avg_daily_consumption || 0).toFixed(2)} <span className="text-[10px] font-bold text-gray-400 uppercase">un/día</span>
                                        </span>
                                    </div>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 font-medium mb-1 flex items-center gap-1.5">
                                            <TrendingUp size={12} className={status.iconColor} /> Agotamiento
                                        </span>
                                        <span className={`text-lg font-black ${status.iconColor}`}>
                                            {product.days_until_stockout === 999 ? '> 30' : product.days_until_stockout} <span className="text-[10px] font-bold uppercase">días</span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Mix/Combined Info Alert */}
                            {product.is_mix && (
                                <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-start gap-3">
                                    <div className="p-1.5 bg-indigo-100 text-indigo-600 rounded-lg">
                                        <Package size={14} />
                                    </div>
                                    <div className="space-y-0.5">
                                        <p className="text-xs font-black text-indigo-900 uppercase">Cálculo de Mix Activado</p>
                                        <p className="text-[11px] text-indigo-700 leading-relaxed font-medium">
                                            Este precio y costo incluyen automáticamente la **Mezcla B ({product.mix_component})**.
                                            La rentabilidad es el resultado del conjunto sabor + leche.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right Column: Configuration & Management */}
                        <div className="space-y-8">
                            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest flex items-center gap-2">
                                    <Save size={16} /> Configuración de Parámetros
                                </h4>
                            </div>

                            <div className="space-y-6">
                                <div className="grid grid-cols-2 gap-6">
                                    <div className="space-y-2">
                                        <label className="text-xs font-black text-gray-600 uppercase tracking-wider block">Stock Mínimo</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                <Package size={14} className="text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                            </div>
                                            <input
                                                type="number"
                                                min="0"
                                                value={config.min_inventory_qty}
                                                onChange={(e) => setConfig({ ...config, min_inventory_qty: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all text-sm font-bold outline-none disabled:opacity-50"
                                                disabled={readOnly}
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs font-black text-gray-600 uppercase tracking-wider block">Tamaño Pack</label>
                                        <div className="relative group">
                                            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                <Package size={14} className="text-gray-400 group-focus-within:text-blue-500 transition-colors" />
                                            </div>
                                            <input
                                                type="number"
                                                min="1"
                                                value={config.pack_size}
                                                onChange={(e) => setConfig({ ...config, pack_size: e.target.value })}
                                                className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all text-sm font-bold outline-none disabled:opacity-50"
                                                placeholder="un/caja"
                                                disabled={readOnly}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-gray-600 uppercase tracking-wider block">Proveedor Principal</label>
                                    <input
                                        type="text"
                                        value={config.supplier}
                                        onChange={(e) => setConfig({ ...config, supplier: e.target.value })}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 focus:bg-white transition-all text-sm font-bold outline-none disabled:opacity-50"
                                        placeholder="Nombre del proveedor o fabricante"
                                        disabled={readOnly}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-black text-gray-600 uppercase tracking-wider block">Costo de Compra (Unitario Net)</label>
                                    <div className="relative group">
                                        <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                            <DollarSign size={14} className="text-gray-400 group-focus-within:text-green-500 transition-colors" />
                                        </div>
                                        <input
                                            type="number"
                                            min="0"
                                            value={config.purchasing_price}
                                            onChange={(e) => setConfig({ ...config, purchasing_price: e.target.value })}
                                            className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-4 focus:ring-green-500/10 focus:border-green-500 focus:bg-white transition-all text-sm font-bold outline-none disabled:opacity-50"
                                            placeholder="0.00"
                                            disabled={readOnly}
                                        />
                                    </div>
                                    <p className="text-[10px] text-gray-400 italic">Este valor afecta el cálculo de rentabilidad real mostrado a la izquierda.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-5 bg-gray-50/80 backdrop-blur-sm border-t border-gray-100 flex justify-end items-center gap-4">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 hover:bg-gray-200/50 rounded-xl transition-all"
                    >
                        {readOnly ? 'Cerrar' : 'Cancelar'}
                    </button>
                    {!readOnly && (
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-8 py-2.5 text-sm font-black text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-xl shadow-lg shadow-blue-500/20 hover:shadow-blue-500/30 transition-all flex items-center gap-2 group"
                        >
                            {saving ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <Save size={18} className="group-hover:scale-110 transition-transform" />
                            )}
                            {saving ? 'GUARDANDO...' : 'GUARDAR CAMBIOS'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsModal;
